import { EMPTY, Observable, catchError, filter, map, of, switchMap, take, tap, throwError, timeout, timer } from 'rxjs';
import { ExportByTypeService } from './export-resolver.model';
import { ExportExecutableParams } from '../export.model';
import { Inject, Injectable, signal } from '@angular/core';
import { HttpStreamsService } from '@cbre/http';
import { saveAs } from 'file-saver';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TRANSLATOR, Translator } from '@miq/utils';
import { getFullFileName, TemplateHubStatus } from './utils';
import { StackPlanViewStateService } from '../../../../state/sp-view-state.service';
import { togglePointFunction } from '@cbre/feature-toggles';
import { PV_SIDE_BY_SIDE_TEMPLATES_TOKEN, PvSideBySideTemplates } from '../../../../stacking-plan.token';
import { HttpParams } from '@angular/common/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable({ providedIn: 'root' })
export class PVExportPDFService implements ExportByTypeService<ExportExecutableParams, boolean> {
  public isPvReportDownloading = signal(false);

  constructor(
    @Inject(TRANSLATOR) private translator: Translator,
    @Inject(PV_SIDE_BY_SIDE_TEMPLATES_TOKEN) private pvSideBySideTemplates: PvSideBySideTemplates,
    private httpClient: HttpStreamsService,
    private notificationSrv: NotificationService,
    private spStateView: StackPlanViewStateService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  @togglePointFunction('isPvTemplateHubEnabled', 'executeTH')
  execute(exportParams: ExportExecutableParams): Observable<boolean> {
    const { propertyId, dialogOptions } = exportParams;
    const target = this.spStateView.getState()?.propertyType ?? 'All';
    const params: any = {
      target,
      smooth: this.spStateView.getState()?.isSmoothView ?? false,
      show_nr: this.spStateView.getState()?.showNonRentableSpace ?? false,
      show_un: this.spStateView.getState()?.showUnaccountedSpace ?? false,
    };
    if (dialogOptions?.user_conf) {
      params.user_conf = dialogOptions.user_conf;
    }
    return this.httpClient
      .httpGet(`/stack3/${propertyId}/pv/download?templateId=${this.pvSideBySideTemplates.TemplafyId}`, null, {
        responseType: 'blob',
        params,
      })
      .pipe(
        switchMap(file => {
          if (file && file.type === 'application/pdf') {
            const fileBlob = new Blob([file], { type: file.type });
            const fileName = `${getFullFileName(exportParams, 'Stack Overview')}.pdf`;
            saveAs(fileBlob, fileName);
            return of(true);
          }
          throw new Error(this.translator.translate('pv.export.errorWithFile'));
        }),
        catchError(() => {
          this.notificationSrv.showSnackBack(this.translator.translate('pv.export.generalError'), '');
          return of(false);
        })
      );
  }

  executeTH(exportParams: ExportExecutableParams): Observable<boolean> {
    const { propertyId } = exportParams;
    const target = this.spStateView.getState()?.propertyType ?? 'All';
    const params: Record<string, any> = {
      target,
      smooth: this.spStateView.getState()?.isSmoothView ?? false,
      show_nr: this.spStateView.getState()?.showNonRentableSpace ?? false,
      show_un: this.spStateView.getState()?.showUnaccountedSpace ?? false,
    };
    if (exportParams?.dialogOptions?.user_conf) {
      params.user_conf = exportParams.dialogOptions.user_conf;
    }
    this.isPvReportDownloading.set(true);

    return this._getTemplateIdForEnv().pipe(
      switchMap(this._makeGenerateReportRequest(propertyId, params)),
      switchMap(cleanedId => this.downloadTemplateHubReport(cleanedId, params)),
      map(response => response.body),
      tap(file => {
        if (file && file.type === 'application/pdf') {
          const fileBlob = new Blob([file], { type: file.type });
          const fileName = `${getFullFileName(exportParams, 'Stack Overview')}.pdf`;
          saveAs(fileBlob, fileName);
          this.isPvReportDownloading.set(false);
          return of(true);
        }
        this.isPvReportDownloading.set(false);
        throw new Error(this.translator.translate('pv.export.errorWithFile'));
      }),
      catchError(() => {
        this.isPvReportDownloading.set(false);
        this.notificationSrv.showSnackBack(this.translator.translate('pv.export.generalError'), '');
        return of(false);
      })
    );
  }

  private _makeGenerateReportRequest = (propertyId: number, params: Record<string, any>) => (templateId: string) => {
    const options = { params };
    return this.httpClient.httpGet(`/stack3/${propertyId}/pv/generate?templateId=${templateId}`, null, options);
  };

  private _getTemplateIdForEnv(): Observable<string> {
    const envId = this.pvSideBySideTemplates.EnvId;
    const templateHubId = this.pvSideBySideTemplates.TemplateHubId;
    const params = new HttpParams({ fromObject: { envId } });

    return this.httpClient.httpGet(`/reports/templatehub/folder/${templateHubId}`, params).pipe(
      map(response => {
        const templates = response?.results ?? [];
        if (templates.length === 0) {
          throw new Error(`No active template found for env ${envId}`);
        }

        if (templates.length > 1) this.logger.warn('Multiple templates found, using the first one');

        return templates[0].id;
      })
    );
  }

  private checkReportStatus(generatedReportId: string): Observable<any> {
    const checkReportStatusUri = `/reports/templatehub/status/${generatedReportId}`;
    return this.httpClient.httpGet(checkReportStatusUri).pipe(
      switchMap(status => {
        if (status === TemplateHubStatus.Failed) {
          return throwError(() => new Error('Report generation failed'));
        }
        if (status === TemplateHubStatus.Completed) {
          return of(true);
        }
        return EMPTY;
      }),
      filter(Boolean),
      take(1)
    );
  }

  private downloadTemplateHubReport(generatedReportId: string, params): Observable<any> {
    const downloadReportUri = `/reports/templatehub/download/${generatedReportId}`;
    const maxPollingTime = 5 * 60 * 1000; // 5 minutes in milliseconds

    // Start polling immediately and every 5 seconds
    return timer(0, 5000).pipe(
      switchMap(() => this.checkReportStatus(generatedReportId)),
      timeout(maxPollingTime), // Fail if polling exceeds 5 minutes
      switchMap(() =>
        this.httpClient.httpGet(downloadReportUri, null, { responseType: 'blob', observe: 'response', params })
      ),
      take(1)
    );
  }
}
